import { Component, EventEmitter, Output, forwardRef, ViewChild, NgZone, ChangeDetectorRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CardListConfig } from '../../crud-view/model/card-data-map-config';
import { cloneDeep } from 'lodash';
import * as lodash from 'lodash';
import { CardBuilderSettingsComponent } from '../card-builder-settings/card-builder-settings.component';
import { NotificationService } from '../../../services/notification/notification.service';
import { AlertTypes } from '../../../utils/alert/alert-types';
import { TextComponentConfig, TextMode } from '../../../utils/text';
import { HeadingType, HeadingComponentConfig } from '../../../utils/heading';
import { ActionBehavior } from '../../../utils/action-utils';
import { SelectableOptionWithTooltip } from '../../../utils/form-utils/selectable-option.type';
import { ButtonComponentConfig } from '../../../utils/button/button-component-config.interface';
import { IconType, IconComponentConfig } from '../../../utils/icon';

@Component({
  selector: 'rappider-card-builder',
  templateUrl: './card-builder.component.html',
  styleUrls: ['./card-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CardBuilderComponent),
      multi: true
    }
  ]
})
export class CardBuilderComponent implements ControlValueAccessor {
  @Output() blur = new EventEmitter<CardListConfig>();
  @Input() entityFields: SelectableOptionWithTooltip[] = [];
  @Input() apiPath: string;

  @ViewChild(CardBuilderSettingsComponent) cardBuilderSettingsComponent: CardBuilderSettingsComponent;

  // Data model
  _value: CardListConfig;
  tempValue: CardListConfig;
  tempValueWithoutChanges: CardListConfig;
  jsonModalBackupData: CardListConfig;

  // Initial config
  initialCardConfig: CardListConfig = {
    titleFieldName: '',
    descriptionFieldName: '',
    subtitleFieldNames: [],
    cardItemClickBehavior: ActionBehavior.Emit,
    showSearchWithAiButton: false
  };

  /* holds the modal visibility state */
  isEditModalVisible = false;
  /* holds the JSON editor modal visibility state */
  isJsonEditorModalVisible = false;
  modalAutoClose = true;
  modalInitialized = false;
  populatedData: Record<string, unknown>[] = [];

  /* validation properties */
  validationErrors: { id: string; message: string }[] = [];
  validationAlertVisible = false;
  validationAlertConfig = {
    type: AlertTypes.Error,
    title: {
      type: HeadingType.H5,
      content: 'Required Fields Missing'
    },
    description: <TextComponentConfig>{ text: '' },
    closeable: false,
    showIcon: true
  };

  get value() {
    return this._value;
  }

  set value(value: CardListConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  /**
   * Get card preview config from populated data
   */
  get cardPreviewConfig() {
    const cardData = this.populatedData?.[0];
    if (!cardData) {
      return null;
    }

    const config: {
      title: HeadingComponentConfig;
      subtitles: HeadingComponentConfig[];
      description: TextComponentConfig | null;
      editButton: ButtonComponentConfig | null;
      deleteButton: ButtonComponentConfig | null;
      iconConfig?: IconComponentConfig;
      iconName?: string;
    } = {
      title: this.getTitle(cardData),
      subtitles: this.getSubtitles(cardData),
      description: this.getDescription(cardData),
      editButton: this.getEditButton(),
      deleteButton: this.getDeleteButton(),
      iconConfig: this.getIconConfig(),
      iconName: this.getIconConfig() ? undefined : this.getIconName()
    };

    return config;
  }

  constructor(
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CardListConfig) {
    if (!value) {
      if (!this.tempValue) {
        this.tempValue = cloneDeep(this.initialCardConfig);
      }
      this._value = cloneDeep(this.initialCardConfig);
    } else {
      this._value = cloneDeep(value);
      const restoredValue = this.restoreFunctions(cloneDeep(value));
      this.tempValue = restoredValue;
    }
    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
  }

  /**
   * Restore functions from backend
   * Converts function strings back to executable functions
   */
  private restoreFunctions(config: CardListConfig): CardListConfig {
    if (!config) {
      return config;
    }

    const restored = { ...config };

    // Restore description function only if string exists and is not empty
    if (restored.descriptionFunctionString && restored.descriptionFunctionString.trim()) {
      try {
        // eslint-disable-next-line no-eval
        restored.getDescriptionFunction = eval(`(${restored.descriptionFunctionString})`);
      } catch (error) {
        restored.getDescriptionFunction = undefined;
      }
    }

    // Restore subtitle function only if string exists and is not empty
    if (restored.subtitleFunctionString && restored.subtitleFunctionString.trim()) {
      try {
        // eslint-disable-next-line no-eval
        restored.getSubTitlesFunction = eval(`(${restored.subtitleFunctionString})`);
      } catch (error) {
        restored.getSubTitlesFunction = undefined;
      }
    }

    return restored;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onItemsChange() {
    if (this.tempValue) {
      const restoredValue = this.restoreFunctions(cloneDeep(this.tempValue));
      this.tempValue = restoredValue;
    }
    this.generatePreviewData();
    if (this.validationAlertVisible) {
      this.updateValidationMessages();
    }
  }

  onCardConfigSave() {
    this.validationErrors = [];

    if (!this.validateConfig(true)) {
      this.modalAutoClose = false;

      this.ngZone.run(() => {
        this.modalAutoClose = true;
      });
      return;
    }

    this.validationAlertVisible = false;
    this.validationErrors = [];
    this.modalAutoClose = true;

    this._value = cloneDeep(this.tempValue);
    this.onChange(this._value);
    this.onTouched();
    this.blur.emit(this._value);

    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    this.closeEditModal();
  }

  onCancel() {
    this.closeEditModal();
  }

  /**
   * Open modal to edit card configuration
   */
  openEditModal() {
    this.validationErrors = [];
    this.validationAlertVisible = false;
    this.modalInitialized = false;

    if (this._value) {
      const restoredValue = this.restoreFunctions(cloneDeep(this._value));
      this.tempValue = restoredValue;
    } else {
      this.tempValue = cloneDeep(this.initialCardConfig);
    }

    this.tempValueWithoutChanges = cloneDeep(this.tempValue);

    this.generatePreviewData();

    this.isEditModalVisible = true;
    this.cdr.detectChanges();
  }

  /**
   * Handler for modal after open event
   */
  onCardModalAfterOpen() {
    if (this.modalInitialized) {
      return;
    }

    this.modalInitialized = true;
    this.cdr.detectChanges();
  }

  /**
   * Close card edit modal and reset states
   */
  closeEditModal() {
    this.isEditModalVisible = false;
    this.validationErrors = [];
    this.validationAlertVisible = false;
    this.modalInitialized = false;
  }

  /**
   * Handle populated data changes
   */
  onPopulatedDataChange(populatedData: Record<string, unknown>[]) {
    this.populatedData = populatedData;
  }

  /**
   * Handle data change from JSON editor
   */
  onDataChange(event: any) {
    if (event) {
      this.tempValue = cloneDeep(event);
      this.value = cloneDeep(this.tempValue);
      this.checkIfErrorsResolved();
    }
  }

  /**
   * Open JSON editor modal
   */
  openJsonEditorModal() {
    if (!this.tempValue) {
      this.tempValue = cloneDeep(this._value || this.initialCardConfig);
    }
    this.jsonModalBackupData = cloneDeep(this.tempValue);
    this.isJsonEditorModalVisible = true;
    this.cdr.detectChanges();
  }

  /**
   * Close JSON editor modal
   */
  closeJsonEditorModal() {
    this.isJsonEditorModalVisible = false;

    if (!this.tempValue) {
      this.tempValue = this.jsonModalBackupData;
    }

    this.value = this.tempValue;
    this.tempValueWithoutChanges = this.tempValue;
    this.checkIfErrorsResolved();
  }

  /**
   * Handle JSON editor visibility change
   */
  onJsonEditorVisibleChange(visible: boolean) {
    this.isJsonEditorModalVisible = visible;

    if (!visible) {
      if (!this.tempValue) {
        this.tempValue = this.jsonModalBackupData;
      }

      this.value = this.tempValue;
      this.tempValueWithoutChanges = this.tempValue;
      this.checkIfErrorsResolved();
    }
  }

  // Private methods

  /**
   * Update validation messages based on current field values
   */
  private updateValidationMessages() {
    const missingFields: string[] = [];

    // Check edit action button
    if (this.tempValue?.editItemActionButton && !this.tempValue.editItemActionButton.name) {
      missingFields.push('Edit Item Action Button: Name is required');
    }

    // Check delete action button
    if (this.tempValue?.deleteItemActionButton && !this.tempValue.deleteItemActionButton.name) {
      missingFields.push('Delete Item Action Button: Name is required');
    }

    if (missingFields.length > 0) {
      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: missingFields.join('\n') },
        showIcon: true,
        closeable: false
      };
    } else {
      this.validationAlertVisible = false;
      this.validationErrors = [];
    }
  }

  /**
   * Validate card configuration
   */
  private validateConfig(isSaveOperation = false): boolean {
    const missingFields: string[] = [];

    // Check edit action button
    if (this.tempValue?.editItemActionButton && !this.tempValue.editItemActionButton.name) {
      missingFields.push('Edit Item Action Button: Name is required');
    }

    // Check delete action button
    if (this.tempValue?.deleteItemActionButton && !this.tempValue.deleteItemActionButton.name) {
      missingFields.push('Delete Item Action Button: Name is required');
    }

    if (missingFields.length > 0) {
      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: missingFields.join('\n') },
        showIcon: true,
        closeable: false
      };

      if (isSaveOperation) {
        this.validationAlertVisible = true;
        this.notificationService.createNotification(
          'error',
          'Validation Error',
          'Please complete all required fields to save the configuration.'
        );
      }

      return false;
    }

    return true;
  }

  /**
   * Check if errors are resolved with recent changes
   */
  private checkIfErrorsResolved() {
    if (!this.validationAlertVisible) {
      return;
    }

    const isValid = this.validateConfig(false);

    if (isValid) {
      this.validationAlertVisible = false;
      this.validationErrors = [];
    }
  }

  /**
   * Generate preview data for card display
   */
  private generatePreviewData() {
    if (!this.tempValue) {
      return;
    }

    const sampleData: Record<string, unknown> = {
      id: '1',
      color: '#1890ff'
    };

    const formatFieldName = (fieldName: string): string => {
      if (!fieldName) {
        return '';
      }
      return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    };

    if (this.tempValue.titleFieldName) {
      const formattedFieldName = formatFieldName(this.tempValue.titleFieldName);
      sampleData[this.tempValue.titleFieldName] = `${formattedFieldName}`;
    }

    if (this.tempValue.descriptionFieldName) {
      const formattedFieldName = formatFieldName(this.tempValue.descriptionFieldName);
      sampleData[this.tempValue.descriptionFieldName] = `${formattedFieldName}`;
    }

    // Add fields for subtitle field names
    if (this.tempValue.subtitleFieldNames && this.tempValue.subtitleFieldNames.length > 0) {
      this.tempValue.subtitleFieldNames.forEach((fieldName: string) => {
        if (fieldName !== this.tempValue.titleFieldName) {
          if (fieldName !== this.tempValue.descriptionFieldName) {
            const formattedFieldName = formatFieldName(fieldName);
            sampleData[fieldName] = `${formattedFieldName}`;
          }
        }
      });
    }

    // Add fields for subtitle function - extract field names from function string
    if (this.tempValue.subtitleFunctionString || this.tempValue.getSubTitlesFunction) {
      const fieldsFromFunction = this.extractFieldNamesFromSubtitleFunction();
      fieldsFromFunction.forEach((fieldName: string) => {
        if (!sampleData[fieldName]) {
          const formattedFieldName = formatFieldName(fieldName);
          sampleData[fieldName] = `${formattedFieldName}`;
        }
      });
    }

    // Add fields for description function - extract field names from function string
    if (this.tempValue.descriptionFunctionString || this.tempValue.getDescriptionFunction) {
      const fieldsFromFunction = this.extractFieldNamesFromDescriptionFunction();
      fieldsFromFunction.forEach((fieldName: string) => {
        if (!sampleData[fieldName]) {
          const formattedFieldName = formatFieldName(fieldName);
          sampleData[fieldName] = `${formattedFieldName}`;
        }
      });
    }

    this.populatedData = [sampleData];
    this.onPopulatedDataChange(this.populatedData);
  }

  /**
   * Extract field names from subtitle function string
   */
  private extractFieldNamesFromSubtitleFunction(): string[] {
    const fields: string[] = [];
    const functionString = this.tempValue?.subtitleFunctionString || '';

    if (!functionString) {
      return fields;
    }

    const fieldPattern = /item\.([a-zA-Z_$][a-zA-Z0-9_$]*)|item\[['"]([^'"]+)['"]\]/g;
    let match;

    while ((match = fieldPattern.exec(functionString)) !== null) {
      const fieldName = match[1] || match[2];
      if (fieldName && !fields.includes(fieldName)) {
        fields.push(fieldName);
      }
    }

    if (fields.length === 0 && this.entityFields && this.entityFields.length > 0) {
      return this.entityFields.slice(0, 2).map(f => f.value || f.key || '').filter(Boolean);
    }

    return fields;
  }

  /**
   * Extract field names from description function string
   */
  private extractFieldNamesFromDescriptionFunction(): string[] {
    const fields: string[] = [];
    const functionString = this.tempValue?.descriptionFunctionString || '';

    if (!functionString) {
      return fields;
    }

    const fieldPattern = /item\.([a-zA-Z_$][a-zA-Z0-9_$]*)|item\[['"]([^'"]+)['"]\]/g;
    let match;

    while ((match = fieldPattern.exec(functionString)) !== null) {
      const fieldName = match[1] || match[2];
      if (fieldName && !fields.includes(fieldName)) {
        fields.push(fieldName);
      }
    }

    // If no fields found, use entityFields
    if (fields.length === 0 && this.entityFields && this.entityFields.length > 0) {
      const descriptionField = this.entityFields.find(f =>
        f.value?.toLowerCase().includes('description') ||
        f.key?.toLowerCase().includes('description')
      );
      if (descriptionField) {
        return [descriptionField.value || descriptionField.key || ''].filter(Boolean);
      }
      return this.entityFields.slice(0, 1).map(f => f.value || f.key || '').filter(Boolean);
    }

    return fields;
  }

  /**
   * Get title from card data
   */
  private getTitle(cardData: Record<string, unknown>): HeadingComponentConfig {
    if (this.tempValue?.titleFieldName) {
      const title = cardData[this.tempValue.titleFieldName] || lodash.get(cardData, this.tempValue.titleFieldName);
      if (title) {
        return { content: String(title), type: HeadingType.H5 };
      }
    }
    return {
      content: 'Card Title',
      type: HeadingType.H5
    };
  }

  /**
   * Get subtitles from card data
   */
  private getSubtitles(cardData: Record<string, unknown>): HeadingComponentConfig[] {
    if (this.tempValue?.getSubTitlesFunction && typeof this.tempValue.getSubTitlesFunction === 'function') {
      try {
        const result = this.tempValue.getSubTitlesFunction(cardData);
        if (Array.isArray(result)) {
          return result;
        }
        return [];
      } catch (error) {
        return [];
      }
    }

    // Fallback to subtitle field names
    if (this.tempValue?.subtitleFieldNames?.length) {
      return this.tempValue.subtitleFieldNames.map((fieldName: string) => {
        const value = cardData[fieldName] || lodash.get(cardData, fieldName) || '';
        return {
          content: Array.isArray(value) ? value.join(', ') : String(value),
          type: HeadingType.H6
        };
      });
    }

    return [];
  }

  /**
   * Get description from card data
   */
  private getDescription(cardData: Record<string, unknown>): TextComponentConfig | null {
    if (!cardData) {
      return null;
    }

    // Use getDescriptionFunction if available and valid
    if (this.tempValue?.getDescriptionFunction && typeof this.tempValue.getDescriptionFunction === 'function') {
      try {
        const result = this.tempValue.getDescriptionFunction(cardData);
        if (result === null || result === undefined) {
          return null;
        }
        if (Array.isArray(result)) {
          if (result.length === 0) {
            return null;
          }
          const firstItem = result[0];
          if (firstItem && typeof firstItem === 'object' && firstItem.content !== undefined) {
            return {
              content: firstItem.content,
              textMode: firstItem.textMode || TextMode.Html
            };
          }
          return {
            content: result as any // eslint-disable-line @typescript-eslint/no-explicit-any
          };
        }
        if (typeof result === 'string') {
          const text = result.trim();
          return text ? { content: text, text, textMode: TextMode.Html } : null;
        }
        if (typeof result === 'object' && result.content !== undefined) {
          return result as TextComponentConfig;
        }
        if (typeof result === 'object' && result !== null) {
          return {
            content: result as any // eslint-disable-line @typescript-eslint/no-explicit-any
          };
        }
      } catch (error) {
        return null;
      }
    }

    let descriptionValue: any = null; // eslint-disable-line @typescript-eslint/no-explicit-any

    if (this.tempValue?.descriptionFieldName) {
      const fieldName = this.tempValue.descriptionFieldName;
      descriptionValue = cardData[fieldName] ?? lodash.get(cardData, fieldName);
    }

    // If no description value found, return null
    if (descriptionValue === null || descriptionValue === undefined || descriptionValue === '') {
      return null;
    }

    // Handle objects - return as object for JSON viewer
    if (typeof descriptionValue === 'object' && descriptionValue !== null) {
      return {
        content: descriptionValue as any // eslint-disable-line @typescript-eslint/no-explicit-any
      };
    }

    // Handle primitives - convert to string
    const descriptionText = String(descriptionValue);
    if (!descriptionText || !descriptionText.trim()) {
      return null;
    }

    return {
      content: descriptionText.trim(),
      text: descriptionText.trim(),
      textMode: TextMode.Html
    };
  }

  /**
   * Get icon config from tempValue
   */
  private getIconConfig(): IconComponentConfig | undefined {
    return this.tempValue?.iconConfig;
  }

  /**
   * Get icon name from iconConfig (for backward compatibility)
   */
  private getIconName(): string {
    if (this.tempValue?.iconConfig?.name) {
      let iconName = this.tempValue.iconConfig.name;
      if (this.tempValue.iconConfig.type === IconType.NgZorro && this.tempValue.iconConfig.theme) {
        iconName = `${iconName} theme-${this.tempValue.iconConfig.theme}`;
      }
      return iconName;
    }
    return '';
  }

  /**
   * Get edit button config
   */
  private getEditButton(): ButtonComponentConfig | null {
    if (!this.tempValue?.editItemActionButton) {
      return null;
    }
    // Check visibility - if visible is false, don't show the button
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const visible = (this.tempValue.editItemActionButton as any)?.visible;
    if (visible === false) {
      return null;
    }
    return this.convertActionToButton(this.tempValue.editItemActionButton);
  }

  /**
   * Get delete button config
   */
  private getDeleteButton(): ButtonComponentConfig | null {
    if (!this.tempValue?.deleteItemActionButton) {
      return null;
    }
    // Check visibility - if visible is false, don't show the button
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const visible = (this.tempValue.deleteItemActionButton as any)?.visible;
    // Only hide if explicitly set to false
    if (visible === false) {
      return null;
    }
    return this.convertActionToButton(this.tempValue.deleteItemActionButton);
  }

  /**
   * Convert Action to ButtonComponentConfig
   */
  private convertActionToButton(action: any): ButtonComponentConfig {
    if (action.type && !action.behavior) {
      return action as ButtonComponentConfig;
    }

    return {
      text: action.text,
      type: action.buttonType,
      icon: action.icon,
      iconPlacement: action.iconPlacement,
      disabled: action.disabled,
      popconfirmTitle: action.popconfirmTitle,
      emitWithoutPopconfirm: action.emitWithoutPopconfirm,
      popconfirmCancelText: action.popconfirmCancelText,
      popconfirmOkText: action.popconfirmOkText,
      popconfirmOkDanger: action.popconfirmOkDanger,
      popconfirmPlacement: action.popconfirmPlacement,
      tooltipText: action.tooltipText,
      behavior: action.behavior,
      redirectUrl: action.redirectUrl,
      redirectTarget: action.redirectTarget as any,
      customCssClass: action.cssClass
    };
  }
}

